import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getMeta, setMeta } from '../data/db';
import type { User } from '../data/types';
import { api, ApiError, NetworkError, setUnauthorizedHandler } from '../lib/api';
import { subscribeConnection, isOnline } from '../sync/connectivity';
import { setPushAllowed, startSyncEngine } from '../sync/engine';
import { setOutboxOwner } from '../sync/outbox';
import { setSessionPaused, setStatusOwner } from '../sync/status';

const SESSION_KEY = 'session-user';
const CONFIG_KEY = 'auth-config';

type AuthStatus = 'loading' | 'signed-out' | 'signed-in';

/** Which sign-in methods the server offers (GET /api/auth/config). */
export interface AuthConfig {
  /** Development username/password sign-in. */
  localLogin: boolean;
  /** Sign-in through the identity provider (Entra External ID). */
  oidc: boolean;
  providerName: string;
  /** Social providers offered as their own buttons (each still signs in through the identity provider). */
  providers?: SocialProvider[];
}

export type SocialProvider = 'google' | 'microsoft';

// Reason codes the server's sign-in callback returns in `?signin_error=`.
const SIGN_IN_ERRORS: Record<string, string> = {
  'not-invited': 'Your account has not been added yet. Ask your administrator to invite the email address you signed in with.',
  inactive: 'Your access has been removed. Contact your administrator.',
  cancelled: 'Sign-in was cancelled.',
  expired: 'That sign-in took too long or was already used. Please try again.',
  unverified: 'Your email address has not been verified with the sign-in provider.',
  unavailable: 'The sign-in service could not be reached. Please try again shortly.',
  failed: 'Sign-in did not complete. Please try again.'
};

interface AuthValue {
  status: AuthStatus;
  user: User | null;
  /** Working from the cached profile because the server could not be reached at start-up. */
  offlineSession: boolean;
  /** The server rejected the session; changes stay queued until the user signs in again. */
  sessionExpired: boolean;
  /** Sign-in methods offered by the server; the last known copy while offline, null if never fetched. */
  config: AuthConfig | null;
  /** Why the last identity-provider sign-in failed, if it did. */
  signInError: string;
  signIn(username: string, password: string): Promise<void>;
  /**
   * Leaves the app for the identity provider's sign-in page (or, with `provider`, straight for that social
   * provider's); it returns to the current page afterwards.
   */
  signInWithProvider(provider?: SocialProvider): void;
  signOut(): Promise<void>;
  refreshUser(): Promise<void>;
  passwordRequired: boolean;
  passwordOpen: boolean;
  openPassword(): void;
  closePassword(): void;
  passwordChanged(): void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [offlineSession, setOfflineSession] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [signInError, setSignInError] = useState('');

  const adopt = useCallback(async (next: User, offline = false) => {
    setOutboxOwner(next.id);
    setStatusOwner(next.id);
    setUser(next);
    setOfflineSession(offline);
    setSessionExpired(false);
    setSessionPaused(false);
    setStatus('signed-in');
    if (!offline) await setMeta(SESSION_KEY, next);
    setPushAllowed(true);
    // Ask the browser not to evict IndexedDB under storage pressure: it holds field work that has not synced yet.
    navigator.storage?.persist?.().catch(() => undefined);
  }, []);

  const verify = useCallback(async () => {
    try {
      const { user: current } = await api<{ user: User | null }>('/api/auth/me', { sessionRequired: false });
      if (current) return adopt(current);
      // The server answered and the session is gone. Keep any queued work; it resumes after signing in.
      const cached = await getMeta<User | null>(SESSION_KEY, null);
      setPushAllowed(false);
      if (cached) { setUser(cached); setOutboxOwner(cached.id); setStatusOwner(cached.id); setSessionExpired(true); setSessionPaused(true); setStatus('signed-out'); }
      else { setUser(null); setStatus('signed-out'); }
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error;
      // No reception: carry on with the last signed-in profile so field work can continue.
      const cached = await getMeta<User | null>(SESSION_KEY, null);
      if (cached) await adopt(cached, true);
      else setStatus('signed-out');
    }
  }, [adopt]);

  const loadConfig = useCallback(async () => {
    try {
      const next = await api<AuthConfig>('/api/auth/config', { sessionRequired: false });
      setConfig(next);
      await setMeta(CONFIG_KEY, next);
    } catch {
      setConfig(await getMeta<AuthConfig | null>(CONFIG_KEY, null));
    }
  }, []);

  // A failed identity-provider sign-in returns to the app with ?signin_error=<reason>; show it once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('signin_error');
    if (!reason) return;
    setSignInError(SIGN_IN_ERRORS[reason] || SIGN_IN_ERRORS.failed);
    params.delete('signin_error');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, []);

  useEffect(() => {
    startSyncEngine();
    setUnauthorizedHandler(() => {
      setPushAllowed(false);
      setSessionPaused(true);
      setSessionExpired(true);
      setStatus('signed-out');
    });
    // The sign-in methods are known before the sign-in screen can appear.
    loadConfig().then(verify).catch((error) => { console.error(error); setStatus('signed-out'); });
    return () => setUnauthorizedHandler(null);
  }, [loadConfig, verify]);

  // An offline session is re-checked with the server as soon as the connection returns.
  useEffect(() => subscribeConnection(() => { if (offlineSession && isOnline()) verify().catch(() => undefined); }), [offlineSession, verify]);

  const signIn = useCallback(async (username: string, password: string) => {
    try {
      const result = await api<{ user: User }>('/api/auth/login', { method: 'POST', body: { username: username.trim().toLowerCase(), password }, sessionRequired: false });
      await adopt(result.user);
      if (result.user.forcePasswordChange) setPasswordOpen(true);
    } catch (error) {
      if (error instanceof NetworkError) throw new Error('You are offline. Connect to the internet to sign in.');
      throw error instanceof ApiError ? error : new Error('Username or password is incorrect.');
    }
  }, [adopt]);

  const signInWithProvider = useCallback((provider?: SocialProvider) => {
    if (!isOnline()) { setSignInError('You are offline. Connect to the internet to sign in.'); return; }
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/api/auth/login/start?returnTo=${encodeURIComponent(returnTo)}${provider ? `&provider=${provider}` : ''}`);
  }, []);

  const signOut = useCallback(async () => {
    const result = await api<{ redirect?: string | null }>('/api/auth/logout', { method: 'POST', sessionRequired: false }).catch(() => null);
    await setMeta(SESSION_KEY, null);
    setPushAllowed(false);
    setUser(null);
    setSessionExpired(false);
    setSessionPaused(false);
    setStatus('signed-out');
    // Identity-provider sessions are ended at the provider too, so a shared device does not sign the next
    // person straight back in as this user. The provider returns to the app's sign-in screen.
    if (result?.redirect) window.location.assign(result.redirect);
  }, []);

  const refreshUser = useCallback(async () => {
    const { user: current } = await api<{ user: User | null }>('/api/auth/me');
    if (current) await adopt(current);
  }, [adopt]);

  const value = useMemo<AuthValue>(() => ({
    status,
    user: status === 'signed-in' ? user : null,
    offlineSession,
    sessionExpired,
    config,
    signInError,
    signIn,
    signInWithProvider,
    signOut,
    refreshUser,
    passwordRequired: Boolean(status === 'signed-in' && user?.forcePasswordChange),
    passwordOpen: passwordOpen || Boolean(status === 'signed-in' && user?.forcePasswordChange && !offlineSession),
    openPassword: () => setPasswordOpen(true),
    closePassword: () => setPasswordOpen(false),
    passwordChanged: () => { setPasswordOpen(false); setUser((current) => (current ? { ...current, forcePasswordChange: false } : current)); setMeta(SESSION_KEY, user ? { ...user, forcePasswordChange: false } : null); }
  }), [status, user, offlineSession, sessionExpired, config, signInError, signIn, signInWithProvider, signOut, refreshUser, passwordOpen]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}

/** The signed-in user. Only use inside the authenticated part of the app. */
export function useUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error('No signed-in user');
  return user;
}
